import { ref } from 'vue'

// Shared swipe/drag/scroll behaviour for horizontally scrollable chip rows (EPG day/category/time chips).
export function useChipScroller () {
  const el = ref<HTMLElement | null>(null)
  const canScrollPrev = ref(false)
  const canScrollNext = ref(false)
  const isDragging = ref(false)
  const dragMoved = ref(false)
  let dragStartX = 0
  let dragStartScrollLeft = 0

  function updateScrollState () {
    const node = el.value
    if (!node) return
    const maxScrollLeft = node.scrollWidth - node.clientWidth
    canScrollPrev.value = node.scrollLeft > 1
    canScrollNext.value = node.scrollLeft < maxScrollLeft - 1
  }

  function scrollByChip (direction: number) {
    const node = el.value
    if (!node) return
    const chipWidth = node.querySelector<HTMLElement>('.bx-epg-chip')?.offsetWidth || 80
    node.scrollBy({ left: direction * chipWidth, behavior: 'smooth' })
  }

  function scrollActiveChipIntoView () {
    const node = el.value
    if (!node) return

    const activeChip = node.querySelector<HTMLElement>('.bx-epg-chip--active')
    if (!activeChip) return

    const containerRect = node.getBoundingClientRect()
    const chipRect = activeChip.getBoundingClientRect()

    if (chipRect.left < containerRect.left) {
      node.scrollLeft = chipRect.left - containerRect.left
    } else if (chipRect.right > containerRect.right) {
      node.scrollLeft = chipRect.right - containerRect.right
    }
  }

  // Unlike scrollActiveChipIntoView (which only nudges the chip back into view if it's cut off), this always
  // aligns the active chip to the container's left edge - used where the active chip isn't naturally near the
  // start of the list, so the swipeability towards later chips is obvious right away (see ThematicHeader.vue).
  function scrollActiveChipToStart () {
    const node = el.value
    if (!node) return

    const activeChip = node.querySelector<HTMLElement>('.bx-epg-chip--active')
    if (!activeChip) return

    const containerRect = node.getBoundingClientRect()
    const chipRect = activeChip.getBoundingClientRect()
    node.scrollLeft += chipRect.left - containerRect.left
  }

  function handleDragMove (event: PointerEvent) {
    if (!isDragging.value) return
    const node = el.value
    if (!node) return
    const delta = event.clientX - dragStartX
    if (Math.abs(delta) > 3) dragMoved.value = true
    node.scrollLeft = dragStartScrollLeft - delta
  }

  function endDrag () {
    isDragging.value = false
    window.removeEventListener('pointermove', handleDragMove)
    window.removeEventListener('pointerup', endDrag)
    window.removeEventListener('pointercancel', endDrag)

    // the click that normally follows a drag's pointerup (if any) is dispatched as its own, earlier task and
    // still needs to see `dragMoved === true` to suppress itself via consumeDragMoved() - but a drag can also
    // end WITHOUT any click ever following (pointercancel, or the pointer released outside the chip), in which
    // case only this fallback actually clears the flag again; otherwise it stays stuck `true` forever and
    // swallows every later interaction (even keyboard activation), not just the one click right after the drag
    setTimeout(() => { dragMoved.value = false }, 0)
  }

  // Reads and immediately resets dragMoved, so it only ever suppresses the one click that follows a drag -
  // callers must use this (not `dragMoved.value` directly) to decide whether to swallow a click, otherwise
  // the flag stays stuck `true` and keeps suppressing every later click/keyboard activation too
  function consumeDragMoved () {
    const moved = dragMoved.value
    dragMoved.value = false
    return moved
  }

  // Desktop: let the mouse drag the chip row horizontally, like a touch swipe
  function startDrag (event: PointerEvent) {
    if (event.pointerType === 'touch') return
    const node = el.value
    if (!node) return
    isDragging.value = true
    dragMoved.value = false
    dragStartX = event.clientX
    dragStartScrollLeft = node.scrollLeft
    window.addEventListener('pointermove', handleDragMove)
    window.addEventListener('pointerup', endDrag)
    window.addEventListener('pointercancel', endDrag)
  }

  return {
    el,
    canScrollPrev,
    canScrollNext,
    isDragging,
    dragMoved,
    updateScrollState,
    scrollByChip,
    scrollActiveChipIntoView,
    scrollActiveChipToStart,
    consumeDragMoved,
    startDrag,
    endDrag
  }
}
